import { OrdersFormFieldViewModel, OrdersFormSectionViewModel } from '@ail/orders-api'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckboxInput } from '../../common/Inputs/CheckboxInput'
import SelectInput from '../../common/Inputs/SelectInput'
import { TextareaInput } from '../../common/Inputs/TextareaInput'
import { TextInput } from '../../common/Inputs/TextInput'

interface IProps {
  validate: boolean
  setValid: (value: boolean) => void
  form: OrdersFormFieldViewModel[]
  sections: OrdersFormSectionViewModel[]
  onUpdate: (data: object) => void
}

type ValidField = {
  name: string
  valid: boolean
}

export const FormBuilder = (props: IProps) => {
  const [data, setData] = useState({})
  const [form, setForm] = useState<OrdersFormFieldViewModel[]>([])
  const [validFields, setValidFields] = useState<ValidField[]>([])

  const { i18n } = useTranslation()

  useEffect(() => {
    setForm([...props.form])
  }, [props.form])

  useEffect(() => {
    props.onUpdate(data)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const getRenderInput = (field: OrdersFormFieldViewModel, index: number) => {
    const value = field.defaultValue

    let visible = true

    const itemList = field.options?.map((o) => {
      const translation = o.tr ? o.tr[i18n.language] ?? o.tr['en'] : o.name;
      return { text: translation, value: o.name }
    })

    const onChange = (value: string) => {
      setData({ ...data, [field.name]: value })
    }

    if (field.tr && field.tr[i18n.language]) {
      field.label = field.tr[i18n.language]
    }

    if (field.visibleWhen) {
      Object.keys(field.visibleWhen).forEach((vw, i) => {
        if (field.visibleWhen && data[vw as keyof typeof data] !== Object.values(field.visibleWhen)[i][0]) {
          visible = false
        }
      })
    }

    const updateValid = (name: string, valid: boolean) => {
      const valids = [...validFields].filter((valid) => valid.name !== name)

      valids.push({ name, valid })
      setValidFields(valids)

      props.setValid(!valids.find((v) => !v.valid))
    }

    switch (field.type) {
      case 'text':
        return (
          <TextInput
            key={index}
            onChange={onChange}
            onValidUpdate={updateValid}
            visible={visible}
            validate={props.validate}
            value={value}
            {...field}
          />
        )
      case 'checkbox':
        return (
          <CheckboxInput
            key={index}
            onChange={onChange}
            visible={visible}
            onValidUpdate={updateValid}
            validate={props.validate}
            value={value}
            {...field}
          />
        )
      case 'textarea':
        return (
          <TextareaInput
            key={index}
            onChange={onChange}
            visible={visible}
            onValidUpdate={updateValid}
            validate={props.validate}
            value={value}
            {...field}
          />
        )
      case 'select':
        return (
          <SelectInput
            key={index}
            itemList={itemList || []}
            onChange={onChange}
            visible={visible}
            onValidUpdate={updateValid}
            validate={props.validate}
            value={value}
            {...field}
          />
        )
    }
  }

  const getRenderSection = (name: string, children: ReactNode, index: number) => {
    const section = props.sections.find((s) => s.name === name);
    const translation = section && section.tr ? section.tr[i18n.language] ?? section.tr['en'] : name;

    return (
      <div key={index} className="col-12 col-md-6">
        <div className="box">
          <div className="boxTitle">{translation}</div>

          {children}
        </div>
      </div>
    )
  }

  const getRenderForm = () => {
    const sectionNames = Array.from(new Set(form.map((f) => f.section)))
    const sections: ReactElement[] = []
    const valids: ValidField[] = []

    sectionNames.forEach((section, sectionIndex) => {
      const fields = form.filter((f) => f.section === section)
      const inputs: ReactElement[] = []

      fields.forEach((field, inputIndex) => {
        const input = getRenderInput({ ...field }, inputIndex)

        valids.push({ name: field.name, valid: !field.required })
        inputs.push(input || <></>)
      })

      sections.push(getRenderSection(section, inputs, sectionIndex))
    })

    if (validFields.length !== valids.length) {
      setValidFields(valids)
    }

    return sections
  }

  return <>{getRenderForm()}</>
}
